import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Toast, ToastService } from './toast.service';

@Component({
    selector: 'app-toast',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <!-- z-[2147483000] : reste au-dessus des modales/backdrops/menus (CDK overlay = 1000, modales projet = 99999).
             Style aligné sur app-alert-banner (Design System /showcase) : fond pastel coloré, bordure gauche, texte coloré. -->
        <div class="fixed top-4 right-4 z-[2147483000] flex flex-col gap-2 pointer-events-none">
            @for (toast of toastService.toasts(); track toast.id) {
                <div
                    class="flex items-start gap-3 px-5 py-3 rounded-xl border-l-4 shadow-md min-w-80 max-w-md animate-slide-in pointer-events-auto"
                    [class]="bannerClasses[toast.type].container">
                    <!-- Icone alignee sur app-alert-banner -->
                    <div class="flex-shrink-0 mt-0.5" [class]="bannerClasses[toast.type].icon">
                        @switch (toast.type) {
                            @case ('success') {
                                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        clip-rule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9
                                        10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                        fill-rule="evenodd" />
                                </svg>
                            }
                            @case ('error') {
                                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        clip-rule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293
                                        1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414
                                        10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                        fill-rule="evenodd" />
                                </svg>
                            }
                            @case ('warning') {
                                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        clip-rule="evenodd"
                                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742
                                        2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012
                                        0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                        fill-rule="evenodd" />
                                </svg>
                            }
                            @case ('info') {
                                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        clip-rule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000
                                        2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                        fill-rule="evenodd" />
                                </svg>
                            }
                        }
                    </div>

                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-semibold" [class]="bannerClasses[toast.type].text">{{ toast.title }}</p>
                        <p class="text-sm mt-0.5" [class]="bannerClasses[toast.type].text">{{ toast.message }}</p>
                        @if (toast.action; as action) {
                            <button
                                class="mt-2 px-3 py-1 text-xs font-semibold rounded-full border transition-colors"
                                type="button"
                                [class]="bannerClasses[toast.type].action"
                                (click)="toastService.triggerAction(toast.id)">
                                {{ action.label }}
                            </button>
                        }
                    </div>

                    <button
                        class="flex-shrink-0 -mt-0.5 -mr-1 opacity-70 hover:opacity-100"
                        type="button"
                        [class]="bannerClasses[toast.type].icon"
                        (click)="toastService.remove(toast.id)">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                        </svg>
                    </button>
                </div>
            }
        </div>
    `,
    styles: [
        `
            @keyframes slide-in {
                from {
                    transform: translateX(100%);
                    opacity: 0;
                }
                to {
                    transform: translateX(0);
                    opacity: 1;
                }
            }
            .animate-slide-in {
                animation: slide-in 0.3s ease-out;
            }
        `,
    ],
})
export class ToastComponent {
    toastService = inject(ToastService);

    /** Classes alignées sur app-alert-banner (Design System /showcase). */
    readonly bannerClasses: Record<Toast['type'], { container: string; icon: string; text: string; action: string }> = {
        success: {
            container: 'bg-green-50 border-l-green-400',
            icon: 'text-green-400',
            text: 'text-green-700',
            action: 'border-green-300 text-green-700 bg-white hover:bg-green-100',
        },
        error: {
            container: 'bg-red-50 border-l-red-400',
            icon: 'text-red-400',
            text: 'text-red-700',
            action: 'border-red-300 text-red-700 bg-white hover:bg-red-100',
        },
        info: {
            container: 'bg-blue-50 border-l-blue-400',
            icon: 'text-blue-400',
            text: 'text-blue-700',
            action: 'border-blue-300 text-blue-700 bg-white hover:bg-blue-100',
        },
        warning: {
            container: 'bg-amber-50 border-l-amber-400',
            icon: 'text-amber-400',
            text: 'text-amber-700',
            action: 'border-amber-300 text-amber-700 bg-white hover:bg-amber-100',
        },
    };
}
